import { useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import api, { formatApiError, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Disclaimer, Spinner, EmptyState, UpgradeGate } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BookHeart, Plus, ImagePlus, Trash2, X, Calendar, Share2 } from "lucide-react";
import { toast } from "sonner";

const MOMENTS = [
  { value: "milestone", label: "Milestone", cls: "bg-kid-grape/12 text-kid-grape" },
  { value: "therapy", label: "Therapy", cls: "bg-kid-teal/12 text-kid-teal" },
  { value: "school", label: "School", cls: "bg-kid-sun/20 text-[#9a7b1e]" },
  { value: "medical", label: "Medical", cls: "bg-kid-coral/12 text-kid-coral" },
  { value: "everyday", label: "Everyday", cls: "bg-kid-mist text-kid-muted" },
  { value: "win", label: "Win", cls: "bg-kid-green/15 text-[#3f9d5c]" },
];
const moment = (v) => MOMENTS.find((m) => m.value === v) || MOMENTS[4];
const MOMENT_HEX = { milestone: "#8B6FD6", therapy: "#1FA5A0", school: "#E0A21C", medical: "#FF7A73", everyday: "#5C6B7E", win: "#5FBE7D" };
const fmtDate = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return d; } };
const SHARE_URL = "https://kidpath.ca/welcome?ref=journal&utm_source=journal_share&utm_medium=qr&utm_campaign=share_a_moment";

export default function Journal() {
  const { isPaid } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [shareEntry, setShareEntry] = useState(null);
  const [sharingId, setSharingId] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const shareRef = useRef(null);

  useEffect(() => {
    QRCode.toDataURL(SHARE_URL, { margin: 1, width: 160, color: { dark: "#1D2B3A", light: "#FFFFFF" } })
      .then(setQrDataUrl).catch(() => {});
  }, []);

  const load = async () => {
    const { data } = await api.get("/journal", { params: filter !== "all" ? { moment_type: filter } : {} });
    setEntries(data); setLoading(false);
  };
  useEffect(() => { if (isPaid) load(); else setLoading(false); }, [isPaid, filter]); // eslint-disable-line

  // Render the branded share card off-screen, capture to PNG, then share/download.
  useEffect(() => {
    if (!shareEntry) return;
    let cancelled = false;
    const run = async () => {
      await new Promise((r) => setTimeout(r, 180));
      const node = shareRef.current;
      if (!node) { setSharingId(null); return; }
      const imgs = Array.from(node.querySelectorAll("img"));
      await Promise.all(imgs.map((img) => (img.complete && img.naturalWidth ? Promise.resolve() : new Promise((res) => { img.onload = res; img.onerror = res; }))));
      try {
        const canvas = await html2canvas(node, { scale: 2, useCORS: true, backgroundColor: null, logging: false });
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        if (cancelled || !blob) return;
        const file = new File([blob], "kidpath-moment.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try { await navigator.share({ files: [file], title: shareEntry.title, text: "A moment from our KidPath journal 💛" }); toast.success("Moment shared."); }
          catch (_) { /* user cancelled share */ }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a"); a.href = url; a.download = "kidpath-moment.png"; a.click();
          URL.revokeObjectURL(url);
          toast.success("Moment image saved to your device.");
        }
      } catch (e) {
        toast.error("Couldn't create the image. Please try again.");
      } finally {
        if (!cancelled) { setSharingId(null); setShareEntry(null); }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [shareEntry]);

  const onShare = (entry) => { setSharingId(entry.id); setShareEntry(entry); };

  if (!isPaid) return (<div><PageHeader eyebrow="Journal" title="Journal" /><UpgradeGate title="The Journal is included in Family Path" body="Upgrade to capture milestones, therapy wins and everyday moments — with photos — all in one private place." /></div>);
  if (loading) return <Spinner label="Loading your journal…" />;

  const confirmDelete = async () => {
    try { await api.delete(`/journal/${deleteEntry.id}`); toast.success("Moment deleted."); setDeleteEntry(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Journal" title="Journal" subtitle="A private timeline of the moments that matter — milestones, wins and everyday joys."
        action={<button onClick={() => setAddOpen(true)} className="kid-btn kid-btn-primary h-11 px-5" data-testid="journal-add-open"><Plus size={16} /> Add moment</button>} />

      <div className="flex flex-wrap gap-2 mb-6" data-testid="journal-filters">
        {[{ value: "all", label: "All" }, ...MOMENTS].map((m) => (
          <button key={m.value} onClick={() => setFilter(m.value)}
            className={`text-sm font-poppins font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${filter === m.value ? "bg-kid-teal text-white border-kid-teal" : "bg-white text-kid-muted border-kid-line hover:bg-kid-mist"}`}
            data-testid={`journal-filter-${m.value}`}>{m.label}</button>
        ))}
      </div>

      {entries.length === 0 ? (
        <EmptyState icon={BookHeart} testid="journal-empty" title={filter === "all" ? "No moments captured yet" : "No moments in this category"}
          body="Document a milestone, a therapy win, a tricky day, or an everyday joy — and add photos to remember it."
          action={<button onClick={() => setAddOpen(true)} className="kid-btn kid-btn-primary h-12 px-7"><Plus size={16} /> Add your first moment</button>} />
      ) : (
        <div className="space-y-4 kid-stagger">
          {entries.map((e) => <EntryCard key={e.id} entry={e} onChanged={load} onDelete={() => setDeleteEntry(e)} onShare={onShare} sharing={sharingId === e.id} />)}
        </div>
      )}

      <div className="mt-6"><Disclaimer>Your journal is private and controlled by you. Photos are stored securely and you can delete any moment or photo at any time.</Disclaimer></div>

      <AddMomentModal open={addOpen} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); load(); }} />

      {shareEntry && (
        <div style={{ position: "fixed", left: "-9999px", top: 0, pointerEvents: "none" }} aria-hidden="true">
          <div ref={shareRef}><ShareMomentCard entry={shareEntry} qrDataUrl={qrDataUrl} /></div>
        </div>
      )}

      <AlertDialog open={!!deleteEntry} onOpenChange={() => setDeleteEntry(null)}>
        <AlertDialogContent data-testid="delete-entry-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this moment?</AlertDialogTitle>
            <AlertDialogDescription>This removes the entry and its photos from your journal. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-kid-coral hover:bg-kid-coral/90" data-testid="delete-entry-confirm">Delete moment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EntryCard({ entry, onChanged, onDelete, onShare, sharing }) {
  const m = moment(entry.moment_type);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const addPhotos = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/journal/${entry.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(files.length > 1 ? "Photos added." : "Photo added.");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removePhoto = async (pid) => {
    try { await api.delete(`/journal/${entry.id}/photos/${pid}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="kid-card p-5" data-testid="journal-entry">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-poppins font-semibold px-2.5 py-1 rounded-full ${m.cls}`}>{m.label}</span>
            <span className="text-xs text-kid-muted inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(entry.entry_date)}</span>
          </div>
          <h3 className="font-display font-bold text-kid-navy text-lg mt-2">{entry.title}</h3>
          {entry.note && <p className="text-kid-ink/90 leading-relaxed mt-1 whitespace-pre-wrap">{entry.note}</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onShare(entry)} disabled={sharing} className="text-kid-teal p-2 hover:bg-kid-teal/10 rounded-lg disabled:opacity-50" title="Share this moment" data-testid="journal-entry-share"><Share2 size={16} /></button>
          <button onClick={onDelete} className="text-kid-coral p-2 hover:bg-kid-coral/10 rounded-lg" title="Delete moment" data-testid="journal-entry-delete"><Trash2 size={16} /></button>
        </div>
      </div>

      {entry.photos?.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-4" data-testid="journal-photos">
          {entry.photos.map((p) => (
            <div key={p.id} className="relative group aspect-square rounded-xl overflow-hidden bg-kid-mist">
              <img src={`${API}/journal/photos/${p.id}/download?auth=${getToken()}`} alt={p.file_name || "moment"} loading="lazy" className="w-full h-full object-cover" data-testid="journal-photo" />
              <button onClick={() => removePhoto(p.id)} className="absolute top-1 right-1 bg-black/55 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove photo" data-testid="journal-photo-remove"><X size={13} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <button onClick={() => inputRef.current?.click()} disabled={busy} className="text-sm text-kid-teal font-poppins font-semibold inline-flex items-center gap-1.5" data-testid="journal-add-photo">
          <ImagePlus size={15} /> {busy ? "Uploading…" : "Add photos"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="journal-photo-input" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
      </div>
    </div>
  );
}

function AddMomentModal({ open, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [type, setType] = useState("everyday");
  const [date, setDate] = useState(today);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const reset = () => { setTitle(""); setNote(""); setType("everyday"); setDate(today); setFiles([]); setError(""); };

  const submit = async () => {
    setError("");
    if (!title.trim()) { setError("Please add a title for this moment."); return; }
    setBusy(true);
    try {
      const { data: entry } = await api.post("/journal", { title, note, moment_type: type, entry_date: date });
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/journal/${entry.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Moment saved.");
      reset(); onDone();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent data-testid="add-moment-dialog">
        <DialogHeader><DialogTitle>Add a moment</DialogTitle>
          <DialogDescription>Capture a milestone, therapy win, or everyday joy — and add photos to remember it.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {error && <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</div>}
          <div className="space-y-1.5"><Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="First full sentence!" className="h-11 rounded-xl bg-white" data-testid="moment-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Date</Label>
              <Input type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className="h-11 rounded-xl bg-white" data-testid="moment-date" /></div>
            <div className="space-y-1.5"><Label>Moment type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-11 rounded-xl" data-testid="moment-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MOMENTS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What happened? How did it feel?" className="rounded-xl bg-white" data-testid="moment-note" /></div>
          <div className="space-y-1.5">
            <Label>Photos (optional)</Label>
            <button onClick={() => inputRef.current?.click()} className="w-full border-2 border-dashed border-kid-line rounded-2xl py-6 text-center hover:border-kid-teal transition-colors" data-testid="moment-photo-drop">
              <ImagePlus className="mx-auto text-kid-teal mb-1.5" />
              <p className="text-sm text-kid-navy font-semibold">{files.length ? `${files.length} photo${files.length > 1 ? "s" : ""} selected` : "Add photos"}</p>
              <p className="text-xs text-kid-muted mt-1">JPG, PNG, WEBP or HEIC · up to 15 MB each</p>
            </button>
            <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="moment-photo-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
          </div>
        </div>
        <DialogFooter>
          <button onClick={() => { reset(); onClose(); }} className="kid-btn kid-btn-ghost h-11 px-5">Cancel</button>
          <button onClick={submit} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5" data-testid="moment-submit">{busy ? "Saving…" : "Save moment"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// Branded, off-screen card captured to PNG for sharing. Inline styles for html2canvas reliability.
function ShareMomentCard({ entry, qrDataUrl }) {
  const m = moment(entry.moment_type);
  const hex = MOMENT_HEX[entry.moment_type] || "#5C6B7E";
  const photo = (entry.photos && entry.photos[0]) || null;
  const badgeText = entry.moment_type === "school" ? "#1D2B3A" : "#FFFFFF";
  return (
    <div style={{ width: 540, background: "#FFFFFF", borderRadius: 28, padding: 36, fontFamily: "'Poppins', system-ui, sans-serif", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
        <div style={{ fontFamily: "'Baloo 2', cursive", fontWeight: 800, fontSize: 26, color: "#1D2B3A", letterSpacing: "-0.01em" }}>
          KidPath<span style={{ color: "#1FA5A0" }}>.ca</span>
        </div>
        <div style={{ background: hex, color: badgeText, fontWeight: 700, fontSize: 15, padding: "7px 16px", borderRadius: 999 }}>{m.label}</div>
      </div>

      {photo && (
        <div style={{ width: "100%", height: 360, borderRadius: 22, overflow: "hidden", background: "#F4F9FC", marginBottom: 22 }}>
          <img src={`${API}/journal/photos/${photo.id}/download?auth=${getToken()}`} crossOrigin="anonymous" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </div>
      )}

      <div style={{ color: "#5C6B7E", fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{fmtDate(entry.entry_date)}</div>
      <div style={{ fontFamily: "'Baloo 2', cursive", fontWeight: 800, fontSize: 30, lineHeight: 1.15, color: "#1D2B3A", marginBottom: entry.note ? 12 : 0, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{entry.title}</div>
      {entry.note && (
        <div style={{ color: "#26364A", fontSize: 18, lineHeight: 1.55, whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: photo ? 5 : 12, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{entry.note}</div>
      )}

      <div style={{ borderTop: "1.5px solid #EAF0F4", marginTop: 26, paddingTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ color: "#5C6B7E", fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>
          <div style={{ color: "#1D2B3A", fontWeight: 700 }}><span style={{ color: "#FF7A73", fontSize: 15 }}>♥</span> Captured with KidPath</div>
          <div>Support for families raising neurodivergent kids · Scan to discover</div>
        </div>
        <div style={{ background: "#FFFFFF", padding: 6, borderRadius: 12, border: "1.5px solid #EAF0F4", flexShrink: 0, lineHeight: 0 }}>
          {qrDataUrl && <img src={qrDataUrl} alt="Scan to visit kidpath.ca" width={64} height={64} style={{ display: "block", width: 64, height: 64 }} />}
        </div>
      </div>
    </div>
  );
}
